import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiPhone,
  FiMapPin,
  FiNavigation,
  FiPackage,
  FiCheck,
  FiX,
} from 'react-icons/fi';
import { Card, LoaderComponent } from '../../components/ui';
import driverService, { AvailableDelivery } from '../../services/driver.service';

const statusFlow = [
  { key: 'ATRIBUIDO', label: 'Aceite', action: 'Marcar como recolhido', nextStatus: 'RECOLHIDO' },
  { key: 'RECOLHIDO', label: 'Recolhido', action: 'Iniciar entrega', nextStatus: 'EM_TRANSITO' },
  { key: 'EM_TRANSITO', label: 'Em transito', action: 'Confirmar entrega', nextStatus: 'ENTREGUE' },
  { key: 'ENTREGUE', label: 'Entregue', action: null, nextStatus: null },
];

const DriverDelivery: React.FC = () => {
  const navigate = useNavigate();
  const [delivery, setDelivery] = useState<AvailableDelivery | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [locationWatchId, setLocationWatchId] = useState<number | null>(null);

  const loadDelivery = useCallback(async () => {
    try {
      const res = await driverService.getStats();
      if (res.data.data.activeDelivery) {
        setDelivery(res.data.data.activeDelivery);
      } else {
        navigate('/driver');
      }
    } catch (error) {
      console.error('Error loading delivery:', error);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadDelivery();
  }, [loadDelivery]);

  // GPS tracking durante entrega
  useEffect(() => {
    if (!delivery || !['EM_TRANSITO', 'RECOLHIDO'].includes(delivery.status)) return;

    if (navigator.geolocation) {
      const id = navigator.geolocation.watchPosition(
        (pos) => {
          driverService.updateLocation(
            delivery.id,
            pos.coords.latitude,
            pos.coords.longitude
          ).catch(() => {});
        },
        () => {},
        { enableHighAccuracy: true, maximumAge: 10000, timeout: 15000 }
      );
      setLocationWatchId(id);
      return () => navigator.geolocation.clearWatch(id);
    }
  }, [delivery?.id, delivery?.status]);

  const handleUpdateStatus = async (nextStatus: string) => {
    if (!delivery) return;
    setUpdating(true);
    try {
      await driverService.updateDeliveryStatus(delivery.id, nextStatus);
      if (nextStatus === 'ENTREGUE') {
        if (locationWatchId !== null) navigator.geolocation.clearWatch(locationWatchId);
        navigate('/driver');
      } else {
        await loadDelivery();
      }
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erro ao atualizar');
    } finally {
      setUpdating(false);
    }
  };

  const handleReject = async () => {
    if (!delivery || !window.confirm('Tens a certeza que queres rejeitar esta entrega?')) return;
    setUpdating(true);
    try {
      await driverService.rejectDelivery(delivery.id);
      navigate('/driver');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erro ao rejeitar');
    } finally {
      setUpdating(false);
    }
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-CV', { style: 'currency', currency: 'CVE' }).format(value);

  const currentStep = statusFlow.findIndex((s) => s.key === delivery?.status);
  const nextAction = statusFlow[currentStep];

  if (loading) return <LoaderComponent />;
  if (!delivery) return null;

  return (
    <div className="max-w-2xl mx-auto p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/driver')} className="p-2 hover:bg-gray-100 rounded-lg">
          <FiArrowLeft />
        </button>
        <h1 className="text-xl font-bold">Entrega #{delivery.id}</h1>
      </div>

      {/* Progress bar */}
      <div className="flex items-center gap-1">
        {statusFlow.slice(0, -1).map((step, i) => (
          <div
            key={step.key}
            className={`flex-1 h-2 rounded-full transition-colors ${
              i <= currentStep ? 'bg-pagali-primary' : 'bg-gray-200'
            }`}
          />
        ))}
      </div>
      <p className="text-center text-sm font-medium text-pagali-primary">
        {statusFlow[currentStep]?.label || delivery.status}
      </p>

      {/* Restaurante -> Cliente */}
      <Card className="p-4">
        <div className="flex items-start gap-3">
          <div className="flex flex-col items-center">
            <div className="w-3 h-3 bg-green-500 rounded-full border-2 border-white shadow" />
            <div className="w-0.5 h-16 bg-gray-300" />
            <div className="w-3 h-3 bg-red-500 rounded-full border-2 border-white shadow" />
          </div>
          <div className="flex-1 space-y-4">
            <div>
              <p className="text-xs text-gray-400 uppercase">Recolher em</p>
              <p className="font-semibold">{delivery.restaurantName}</p>
              <p className="text-sm text-gray-600">{delivery.restaurantAddress}</p>
              {delivery.restaurantLat && delivery.restaurantLng && (
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${delivery.restaurantLat},${delivery.restaurantLng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-pagali-primary mt-1"
                >
                  <FiNavigation className="text-xs" /> Navegar
                </a>
              )}
            </div>
            <div>
              <p className="text-xs text-gray-400 uppercase">Entregar em</p>
              <p className="font-semibold">{delivery.customerName}</p>
              <p className="text-sm text-gray-600">{delivery.deliveryAddress}</p>
              {delivery.deliveryLat && delivery.deliveryLng && (
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${delivery.deliveryLat},${delivery.deliveryLng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-pagali-primary mt-1"
                >
                  <FiNavigation className="text-xs" /> Navegar
                </a>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Info pedido */}
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Pedido #{delivery.orderId}</p>
            <p className="text-xl font-bold">{formatCurrency(delivery.orderTotal)}</p>
          </div>
          {delivery.customerPhone && (
            <a
              href={`tel:${delivery.customerPhone}`}
              className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
            >
              <FiPhone /> Ligar
            </a>
          )}
        </div>
      </Card>

      {/* Botoes de acao */}
      <div className="space-y-3 pt-4">
        {nextAction?.action && (
          <button
            onClick={() => handleUpdateStatus(nextAction.nextStatus!)}
            disabled={updating}
            className="w-full py-4 bg-pagali-primary text-white rounded-xl font-semibold text-lg hover:bg-pagali-primary/90 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
          >
            {updating ? (
              '...'
            ) : nextAction.nextStatus === 'ENTREGUE' ? (
              <><FiCheck className="text-xl" /> {nextAction.action}</>
            ) : nextAction.nextStatus === 'RECOLHIDO' ? (
              <><FiPackage className="text-xl" /> {nextAction.action}</>
            ) : (
              <><FiNavigation className="text-xl" /> {nextAction.action}</>
            )}
          </button>
        )}

        {delivery.status === 'ATRIBUIDO' && (
          <button
            onClick={handleReject}
            disabled={updating}
            className="w-full py-3 bg-white border border-red-300 text-red-600 rounded-xl font-medium hover:bg-red-50 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
          >
            <FiX /> Rejeitar entrega
          </button>
        )}
      </div>
    </div>
  );
};

export default DriverDelivery;
